import { CircularProgress } from '@mui/material';
import { toast } from '@crono-one/design-system/side-effects';
import { useAuth } from 'context/auth';
import { FlexDiv } from 'crono-fe-common/components/Layout/FlexDiv';
import PATH from 'crono-fe-common/constants/path';
import { ListType } from 'crono-fe-common/types/list';
import useGetList from 'hooks/services/lists/useGetList';
import { useOpenList } from 'pages/lists/useOpenList';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const LIST_UNAVAILABLE_MESSAGE =
  "This list doesn't exist or hasn't been shared with you";
const LIST_NO_ACCESS_MESSAGE =
  "You don't have access to this list. Ask the list owner to share it with you.";
const LIST_DELETED_MESSAGE = 'This list no longer exists.';
const LIST_OPEN_FAILED_MESSAGE = "Couldn't open this list";

type HttpError = { response?: { status?: number } } | null;

const getUnavailableMessage = (error: unknown) => {
  const status = (error as HttpError)?.response?.status;
  if (status === 403) return LIST_NO_ACCESS_MESSAGE;
  if (status === 404) return LIST_DELETED_MESSAGE;
  return LIST_UNAVAILABLE_MESSAGE;
};

const PAGE_BY_LIST_TYPE: Partial<Record<ListType, string>> = {
  [ListType.Account]: PATH.LISTS,
  [ListType.Prospect]: PATH.LISTS,
  [ListType.Lead]: PATH.LISTS,
  [ListType.Template]: PATH.TEMPLATES,
  [ListType.Strategy]: PATH.STRATEGIES,
};

const parseListId = (value: string | undefined) => {
  const listId = Number(value);
  return Number.isInteger(listId) && listId > 0 ? listId : null;
};

const ListInterceptor = () => {
  const { listId: listIdParam } = useParams();
  const listId = parseListId(listIdParam);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openList } = useOpenList();
  // Waiting for the user keeps a logged-out visitor on this route until the
  // login redirect carries the link along, and stops the open-lists patch from
  // starting off an empty preference list.
  const { data, isError, error } = useGetList(listId, !!user);
  const handledRef = useRef(false);

  const isResolved = !!user && (!listId || isError || !!data);

  useEffect(() => {
    if (!isResolved || handledRef.current) return;
    handledRef.current = true;

    const list = data?.data?.data;
    const page = list ? PAGE_BY_LIST_TYPE[list.type] : undefined;
    const resolve = async () => {
      if (!list || !page) {
        const message = getUnavailableMessage(error);
        toast.error(message, { id: message });
      } else if (!(await openList(list.id, list.type))) {
        toast.error(LIST_OPEN_FAILED_MESSAGE, { id: LIST_OPEN_FAILED_MESSAGE });
      }
      navigate(page ?? PATH.LISTS, { replace: true });
    };
    resolve();
  }, [isResolved, data, error, openList, navigate]);

  return (
    <FlexDiv>
      <CircularProgress />
    </FlexDiv>
  );
};

export default ListInterceptor;
